import {use, useCallback, useSyncExternalStore} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  getRelayConnectionProgress,
  getRelayEntry,
  getRelayReachable,
  subscribeRelayRegistry,
  type RelayConnectionProgressSnapshot,
} from '@github-ui/ahp-relay/relay-registry'
import {useEnvironment} from './use-environment'
import {SessionContext} from '../contexts/SessionContext'
import type {Environment, EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import type {RelayStatus} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {Session} from '../types/session'
import type {EstablishedAhpResumptionAttempt} from '@github-ui/ahp-session-client/contracts'

// Kinds whose hosts can be woken from offline via `/connect`. Codespaces are
// excluded: the web token lacks codespace scope, so waking them 403s.
const WAKEABLE_ENVIRONMENT_KINDS: ReadonlySet<EnvironmentKind> = new Set(['managed-sandbox', 'managed-cca'])

export interface AhpSessionEligibility {
  /**
   * True when the active session is bound to an MC environment. This is an
   * **identity** signal derived synchronously from the session's
   * `environment_id` — it does not wait for the environments query and does not
   * depend on whether the host is currently reachable — so an AHP session is
   * recognised as AHP on first render (correct log renderer, single-session
   * chrome, steering routing, repo-permission bypass) with no flash of the
   * non-AHP UI. Use this for anything that describes what the session *is*.
   */
  isAhpSession: boolean
  /**
   * True when the session is AHP **and** its environment is resolved, speaks AHP,
   * and is currently online, so the live AHP relay can be used. Always implies
   * {@link isAhpSession}. Depends on the environments query, so it may flip from
   * `false` to `true` once that resolves. Use this for anything that needs to
   * reach the host right now: the live relay event source, the native
   * (relay-backed) timeline, and steering dispatch.
   */
  reachable: boolean
  /**
   * True when a wakeable-kind AHP host is offline/waking: mount the relay to
   * fire one `/connect` (wake attempt) while {@link reachable} stays `false`.
   */
  wakeable: boolean
  /** The environment id the session is bound to, when one is known. */
  environmentId: string | undefined
  /** The resolved environment record, when the environments query has populated. */
  environment: Environment | undefined
  /**
   * The class of compute backing the bound environment, when the environments
   * query has populated (e.g. `managed-actions`, `managed-sandbox`,
   * `managed-cca`, `user-local`, `user-codespace`). `undefined` until the
   * environment resolves or when the session is not AHP-backed.
   */
  environmentKind: EnvironmentKind | undefined
  /** True while the environments list is still loading; `reachable` may flip when it resolves. */
  loading: boolean
  /** Initial metadata lookup failure. Cached metadata remains available during failed refreshes. */
  error?: Error | null
  /** Re-run the environment metadata lookup for an explicit user retry. */
  refetchEnvironment?: () => Promise<void>
  /** Identity-matched progress for the current relay connection attempt. */
  connectionProgress?: RelayConnectionProgressSnapshot
  /** Identity-matched current relay status. */
  relayStatus?: RelayStatus
}

/**
 * Pure decision for {@link useIsAhpSession}, split out so it can be unit-tested
 * without wiring the environments query, feature flags, and session context.
 *
 * `isAhpSession` (identity) depends only on the session being bound to an
 * environment (`environmentId`), so it resolves synchronously without the
 * environments query. `reachable` (connectivity) additionally requires the
 * resolved environment to speak AHP and be online.
 */
export function resolveAhpEligibility(
  flagOn: boolean,
  environmentId: string | undefined,
  environment: Environment | undefined,
  relayConnected = false,
): {isAhpSession: boolean; reachable: boolean; wakeable: boolean} {
  const isAhpSession = flagOn && !!environmentId
  const envReachable = !!environment?.capabilities?.ahp_version && environment.status === 'online'
  // A live relay means the host is reachable now, even if the cached environment
  // record still lags at `offline` right after a successful wake.
  const reachable = isAhpSession && (envReachable || relayConnected)
  const wakeable =
    isAhpSession &&
    !relayConnected &&
    !!environment &&
    WAKEABLE_ENVIRONMENT_KINDS.has(environment.kind) &&
    (environment.status === 'offline' || environment.status === 'waking')
  return {isAhpSession, reachable, wakeable}
}

/**
 * Decides whether the active session is AHP-backed and whether its host is
 * currently reachable.
 *
 * Consumers must branch on the right signal: {@link AhpSessionEligibility.isAhpSession}
 * for session identity (log renderer, chrome, routing) and
 * {@link AhpSessionEligibility.reachable} for anything requiring a live relay
 * connection (event streaming, native timeline, steering). Bundling both into
 * one flag would make an offline AHP session silently render as a legacy session.
 *
 * The bound environment is resolved by id via {@link useEnvironment}, not by
 * scanning the list, which omits per-session `managed-sandbox` allocations. Only
 * `reachable` depends on that lookup; identity resolves synchronously from
 * `environment_id`.
 *
 * Gated by the environments feature flag (the same flag that gates the
 * environments query itself).
 */
export function useIsAhpSession(
  session: Session | null | undefined,
  options?: {resumptionAttempt?: EstablishedAhpResumptionAttempt},
): AhpSessionEligibility {
  const environmentId = session?.environment_id
  const taskId = session?.task_id
  const environmentsFlagOn = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const environmentQuery = useEnvironment(environmentId, {
    enabled: environmentsFlagOn && !!environmentId,
    ...(options?.resumptionAttempt ? {resumptionAttempt: options.resumptionAttempt} : {}),
  })
  const {data: environment, error, isLoading, refetch} = environmentQuery
  const refetchEnvironment = useCallback(async () => {
    await refetch({cancelRefetch: false})
  }, [refetch])
  // Treat a connected relay as reachable so a woken host flips online without
  // waiting on the environment record (which can lag at `offline`). Stays true
  // through brief post-connect reconnecting churn.
  const relayConnected = useSyncExternalStore(
    subscribeRelayRegistry,
    () =>
      taskId && environmentId && session?.id
        ? getRelayReachable(taskId, {environmentId, sessionId: session.id})
        : false,
    () => false,
  )
  const connectionProgress = useSyncExternalStore(
    subscribeRelayRegistry,
    () =>
      taskId && environmentId && session?.id
        ? getRelayConnectionProgress(taskId, {environmentId, sessionId: session.id})
        : undefined,
    () => undefined,
  )
  const relayStatus = useSyncExternalStore(
    subscribeRelayRegistry,
    () => {
      if (!taskId || !environmentId || !session?.id) return undefined
      const entry = getRelayEntry(taskId)
      return entry?.environmentId === environmentId && entry.sessionId === session.id ? entry.status : undefined
    },
    () => undefined,
  )
  const {isAhpSession, reachable, wakeable} = resolveAhpEligibility(
    environmentsFlagOn,
    environmentId,
    environment ?? undefined,
    relayConnected,
  )
  return {
    isAhpSession,
    reachable,
    wakeable,
    environmentId,
    environment: environment ?? undefined,
    environmentKind: environment?.kind,
    loading: environmentsFlagOn && !!environmentId && isLoading,
    error: error instanceof Error ? error : error ? new Error('The environment could not be loaded.') : null,
    refetchEnvironment,
    connectionProgress,
    relayStatus,
  }
}

/**
 * Convenience hook for callers that already use {@link SessionContext}.
 * Equivalent to `useIsAhpSession(use(SessionContext)?.session ?? null)`.
 */
export function useIsAhpSessionFromContext(): AhpSessionEligibility {
  const ctx = use(SessionContext)
  return useIsAhpSession(ctx?.session ?? null)
}
